import { cleanBriefingList, type BriefingFields, type BriefingRow, type MetricKey } from "@talentkontor/domain";
import { ApiError, type Api } from "../../../data/api";
import { t } from "../../../lib/sprache";

/**
 * Lader und Schreibwege zur Marke (0073): Freigabe-Links, Beweiskette, Briefing, Kennzahlen mit Screenshots, Berichte.
 * Rechte prüft die Datenbank (Freigaben und Berichte nur über die Funktionen; der Schlüssel eines Links kommt genau einmal
 * zurück und wird nirgends gespeichert).
 */

export interface BrandReviewRow {
  id: string;
  org_id: string;
  deal_id: string;
  period_id: string;
  deliverable_id: string | null;
  submission_id: string;
  contact_id: string | null;
  recipient_name: string | null;
  recipient_email: string;
  language: "de" | "en";
  message: string | null;
  expires_at: string;
  status: "open" | "approved" | "changes_requested" | "withdrawn" | "closed";
  first_opened_at: string | null;
  last_opened_at: string | null;
  open_count: number;
  decided_at: string | null;
  decided_name: string | null;
  decided_comment: string | null;
  revision_round: number | null;
  extra_round: boolean;
  forwarded_at: string | null;
  closed_reason: string | null;
  sent_at: string | null;
  sent_count: number;
  created_at: string;
}
export const REVIEW_SPALTEN =
  "id, org_id, deal_id, period_id, deliverable_id, submission_id, contact_id, recipient_name, recipient_email, language, message, expires_at, status, " +
  "first_opened_at, last_opened_at, open_count, decided_at, decided_name, decided_comment, revision_round, extra_round, forwarded_at, closed_reason, " +
  "sent_at, sent_count, created_at";

export interface BrandReviewEventRow {
  id: number;
  review_id: string;
  event: string;
  at: string;
  ip: string | null;
  user_agent: string | null;
  details: Record<string, unknown>;
}

export interface MetricsRow {
  id: string;
  deal_id: string;
  deliverable_id: string;
  source: "creator" | "team";
  captured_on: string;
  reach: number | null;
  impressions: number | null;
  interactions: number | null;
  link_clicks: number | null;
  views: number | null;
  note: string | null;
  submitted_at: string;
}

export interface BrandReportRow {
  id: string;
  brand_id: string;
  deal_id: string | null;
  campaign_id: string | null;
  title: string;
  intro: string | null;
  language: "de" | "en";
  include_instagram: boolean;
  contact_id: string | null;
  recipient_email: string | null;
  expires_at: string;
  revoked_at: string | null;
  sent_at: string | null;
  sent_count: number;
  view_count: number;
  last_viewed_at: string | null;
  created_at: string;
}
const REPORT_SPALTEN = "id, brand_id, deal_id, campaign_id, title, intro, language, include_instagram, contact_id, recipient_email, expires_at, revoked_at, sent_at, sent_count, view_count, last_viewed_at, created_at";

export interface BrandContactLite {
  id: string;
  brand_id: string;
  name: string;
  email: string | null;
  roles: string[];
}

export interface MetricsFile {
  id: string;
  owner_id: string;
  storage_path: string;
  file_name: string;
  mime_type: string;
  size_bytes: number;
  created_at: string;
}

export interface MarkenEinstellungen {
  brand_review_mode: "after_team" | "direct";
  brand_review_link_days: number;
  brand_feedback_to_creator: boolean;
  brand_review_show_rounds: boolean;
  metrics_request_days: number;
  metrics_request_story_days: number;
  brand_report_link_days: number;
}
export const MARKEN_EINSTELLUNGEN_SPALTEN =
  "brand_review_mode, brand_review_link_days, brand_feedback_to_creator, brand_review_show_rounds, metrics_request_days, metrics_request_story_days, brand_report_link_days";

export interface MarkeDerKooperation {
  reviews: BrandReviewRow[];
  events: BrandReviewEventRow[];
  briefings: BriefingRow[];
  metrics: MetricsRow[];
  metricsFiles: MetricsFile[];
  reports: BrandReportRow[];
  contacts: BrandContactLite[];
  settings: MarkenEinstellungen | null;
}

function quiet<T>(p: Promise<T[]>): Promise<T[]> {
  return p.catch(() => [] as T[]);
}

const BRIEFING_SPALTEN = "id, deal_id, deliverable_id, key_messages, dos, donts, hashtags, mentions, ad_label, paid_partnership, deadline, notes, updated_at";

/** Kontakte der Marke und, wenn eine Agentur sie vertritt, auch deren Kontakte. */
export async function ladeKontakte(api: Api, brandId: string): Promise<BrandContactLite[]> {
  const brand = (await quiet(api.select<{ id: string; represented_by: string | null }>("brands", { columns: "id, represented_by", filters: [["id", "eq", brandId]] })))[0];
  const ids = [brandId, ...(brand?.represented_by ? [brand.represented_by] : [])];
  return quiet(api.select<BrandContactLite>("brand_contacts", { columns: "id, brand_id, name, email, roles", filters: [["brand_id", "in", ids]], order: [["created_at", "asc"]] }));
}

export async function ladeMarkenEinstellungen(api: Api, orgId: string): Promise<MarkenEinstellungen | null> {
  return (await quiet(api.select<MarkenEinstellungen>("org_settings", { columns: MARKEN_EINSTELLUNGEN_SPALTEN, filters: [["org_id", "eq", orgId]] })))[0] ?? null;
}

export async function ladeMarkeDerKooperation(api: Api, orgId: string, deal: { id: string; brand_id: string }): Promise<MarkeDerKooperation> {
  const byDeal = [["deal_id", "eq", deal.id]] as const;
  const [reviews, briefings, metrics, reports, contacts, settings, beitraege] = await Promise.all([
    quiet(api.select<BrandReviewRow>("brand_reviews", { columns: REVIEW_SPALTEN, filters: byDeal, order: [["created_at", "desc"]] })),
    quiet(api.select<BriefingRow>("deal_briefings", { columns: BRIEFING_SPALTEN, filters: byDeal })),
    quiet(api.select<MetricsRow>("deliverable_metrics", { filters: byDeal })),
    quiet(api.select<BrandReportRow>("brand_reports", { columns: REPORT_SPALTEN, filters: byDeal, order: [["created_at", "desc"]] })),
    ladeKontakte(api, deal.brand_id),
    ladeMarkenEinstellungen(api, orgId),
    quiet(api.select<{ id: string }>("deal_deliverables", { columns: "id", filters: byDeal })),
  ]);
  const reviewIds = reviews.map((r) => r.id);
  const ids = beitraege.map((b) => b.id);
  const [events, metricsFiles] = await Promise.all([
    reviewIds.length ? quiet(api.select<BrandReviewEventRow>("brand_review_events", { filters: [["review_id", "in", reviewIds]], order: [["at", "asc"]] })) : Promise.resolve([]),
    ids.length ? quiet(api.select<MetricsFile>("attachments", {
      columns: "id, owner_id, storage_path, file_name, mime_type, size_bytes, created_at",
      filters: [["owner_kind", "eq", "metrics"], ["owner_id", "in", ids], ["deleted_at", "is", null]], order: [["created_at", "asc"]],
    })) : Promise.resolve([]),
  ]);
  return { reviews, events, briefings, metrics, metricsFiles, reports, contacts, settings };
}

/** Offene Freigabe-Links der Agentur (Beitragsübersicht, Übersicht): nur Stand und Frist. */
export async function ladeOffeneFreigaben(api: Api, orgId: string): Promise<Pick<BrandReviewRow, "id" | "deal_id" | "deliverable_id" | "submission_id" | "status" | "expires_at" | "created_at" | "recipient_email" | "first_opened_at">[]> {
  return quiet(api.select("brand_reviews", {
    columns: "id, deal_id, deliverable_id, submission_id, status, expires_at, created_at, recipient_email, first_opened_at",
    filters: [["org_id", "eq", orgId], ["status", "eq", "open"]],
  }));
}

export async function ladeBerichteDerKampagne(api: Api, campaignId: string): Promise<BrandReportRow[]> {
  return quiet(api.select<BrandReportRow>("brand_reports", { columns: REPORT_SPALTEN, filters: [["campaign_id", "eq", campaignId]], order: [["created_at", "desc"]] }));
}

// ── Links ────────────────────────────────────────────────────────────────────

export function linkAdresse(kind: "freigabe" | "bericht", token: string): string {
  const base = typeof window !== "undefined" ? window.location.origin : "";
  return `${base}/${kind}/${token}`;
}

export interface LinkErgebnis {
  id: string;
  token: string;
  expires_at: string;
  recipient: string | null;
  /** Mail an die Marke eingereiht (sonst nur der Link) */
  sent: boolean;
}

export async function gibAnMarke(api: Api, input: {
  submissionId: string; contactId: string | null; email: string | null; name: string | null; message: string | null; language: "de" | "en" | null; days: number | null; send: boolean;
}): Promise<LinkErgebnis> {
  const r = await api.rpc<{ review_id: string; token: string; expires_at: string; recipient: string }>("brand_review_send", {
    p_submission: input.submissionId, p_contact: input.contactId, p_email: input.email?.trim() || null, p_name: input.name?.trim() || null,
    p_message: input.message?.trim() || null, p_language: input.language, p_days: input.days, p_send: input.send,
  });
  return { id: r.review_id, token: r.token, expires_at: r.expires_at, recipient: r.recipient, sent: input.send };
}

export async function erneuereFreigabe(api: Api, reviewId: string, send: boolean): Promise<LinkErgebnis> {
  const r = await api.rpc<{ review_id: string; token: string; expires_at: string; recipient: string }>("brand_review_renew", { p_review: reviewId, p_send: send });
  return { id: r.review_id, token: r.token, expires_at: r.expires_at, recipient: r.recipient, sent: send };
}

export async function zieheFreigabeZurueck(api: Api, reviewId: string, reason: string | null): Promise<void> {
  await api.rpc("brand_review_withdraw", { p_review: reviewId, p_reason: reason?.trim() || null });
}

export async function gibRueckmeldungWeiter(api: Api, reviewId: string, text: string): Promise<void> {
  if (text.trim().length < 3) throw new ApiError(t("Bitte beschreibe die gewünschte Änderung für den Creator.", "Please describe the change you want for the creator."));
  await api.rpc("brand_review_forward", { p_review: reviewId, p_feedback: text.trim() });
}

// ── Briefing ─────────────────────────────────────────────────────────────────

export interface BriefingEingabe {
  key_messages: string[];
  dos: string[];
  donts: string[];
  hashtags: string[];
  mentions: string[];
  ad_label: string;
  paid_partnership: boolean;
  deadline: string;
  notes: string;
}

/** Eingabe prüfen wie die Datenbank; Rückgabe die gesäuberten Felder oder der erste Fehler. */
export function pruefeBriefing(e: BriefingEingabe): { fields: BriefingFields | null; error: string | null } {
  const lists = {
    key_messages: cleanBriefingList(e.key_messages, "text"), dos: cleanBriefingList(e.dos, "text"), donts: cleanBriefingList(e.donts, "text"),
    hashtags: cleanBriefingList(e.hashtags, "hashtag"), mentions: cleanBriefingList(e.mentions, "mention"),
  };
  const bad = Object.values(lists).find((l) => l.error);
  if (bad) return { fields: null, error: bad.error };
  if (e.ad_label.trim().length > 120) return { fields: null, error: t("Die Kennzeichnung darf höchstens 120 Zeichen haben.", "The ad disclosure may have at most 120 characters.") };
  if (e.notes.trim().length > 4000) return { fields: null, error: t("Die Hinweise dürfen höchstens 4000 Zeichen haben.", "The notes may have at most 4000 characters.") };
  return {
    fields: {
      key_messages: lists.key_messages.values, dos: lists.dos.values, donts: lists.donts.values, hashtags: lists.hashtags.values, mentions: lists.mentions.values,
      ad_label: e.ad_label.trim() || null, paid_partnership: e.paid_partnership, deadline: e.deadline || null, notes: e.notes.trim() || null,
    },
    error: null,
  };
}

/** Briefing der Kooperation (ohne Beitrag) oder eines Beitrags anlegen bzw. ändern. */
export async function speichereBriefing(api: Api, input: { orgId: string; dealId: string; deliverableId: string | null; id: string | null; fields: BriefingFields }): Promise<void> {
  if (input.id) {
    const rows = await api.update("deal_briefings", [["id", "eq", input.id]], { ...input.fields }, { columns: "id" });
    if (!rows.length) throw new ApiError(t("Speichern nicht möglich: Das Briefing ist für dein Konto nicht freigegeben.", "Saving not possible: the briefing is not available to your account."), { code: "42501" });
    return;
  }
  await api.insert("deal_briefings", { org_id: input.orgId, deal_id: input.dealId, deliverable_id: input.deliverableId, ...input.fields }, { columns: "id" });
}

export async function loescheBriefing(api: Api, id: string): Promise<void> {
  await api.remove("deal_briefings", [["id", "eq", id]]);
}

// ── Kennzahlen ───────────────────────────────────────────────────────────────

export async function trageKennzahlenEin(api: Api, deliverableId: string, values: Partial<Record<MetricKey, number | null>>, capturedOn: string | null, note: string | null): Promise<void> {
  await api.rpc("deliverable_metrics_submit", { p_deliverable: deliverableId, p_values: values, p_captured_on: capturedOn || null, p_note: note?.trim() || null });
}

const BILD_TYPEN = ["image/png", "image/jpeg", "image/webp", "application/pdf"];

/** Screenshot der Statistik (Pfadregel <agentur>/metrics/<beitrag>/<datei>, 0073); Creator und Team. */
export async function ladeScreenshotHoch(api: Api, orgId: string, deliverableId: string, file: File): Promise<void> {
  if (!BILD_TYPEN.includes(file.type)) throw new ApiError(t("Bitte ein Bild (PNG, JPG, WebP) oder ein PDF wählen.", "Please choose an image (PNG, JPG, WebP) or a PDF."));
  if (file.size <= 0 || file.size > 15_728_640) throw new ApiError(t("Die Datei ist leer oder größer als 15 MB.", "The file is empty or larger than 15 MB."));
  const name = file.name.normalize("NFC").replace(/[^\p{L}\p{N}._-]+/gu, "-").replace(/\.{2,}/g, ".").replace(/^[.-]+/, "").slice(-120) || "screenshot";
  const path = `${orgId}/metrics/${deliverableId}/${Date.now()}-${name}`;
  await api.upload("documents", path, file);
  await api.insert("attachments", {
    org_id: orgId, owner_kind: "metrics", owner_id: deliverableId, storage_path: path, file_name: file.name.slice(0, 200) || "Screenshot", mime_type: file.type, size_bytes: file.size,
  }, { columns: "id", returning: false });
}

// ── Berichte ─────────────────────────────────────────────────────────────────

export async function legeBerichtAn(api: Api, input: {
  dealId: string | null; campaignId: string | null; title: string | null; intro: string | null; language: "de" | "en" | null; contactId: string | null; email: string | null;
  days: number | null; includeInstagram: boolean; send: boolean; message: string | null;
}): Promise<LinkErgebnis> {
  const r = await api.rpc<{ report_id: string; token: string; expires_at: string; recipient: string | null }>("brand_report_create", {
    p_deal: input.dealId, p_campaign: input.campaignId, p_title: input.title?.trim() || null, p_intro: input.intro?.trim() || null, p_language: input.language,
    p_contact: input.contactId, p_email: input.email?.trim() || null, p_days: input.days, p_include_instagram: input.includeInstagram, p_send: input.send,
    p_message: input.message?.trim() || null,
  });
  return { id: r.report_id, token: r.token, expires_at: r.expires_at, recipient: r.recipient, sent: input.send };
}

export async function erneuereBericht(api: Api, reportId: string, send: boolean): Promise<LinkErgebnis> {
  const r = await api.rpc<{ report_id: string; token: string; expires_at: string }>("brand_report_renew", { p_report: reportId, p_send: send });
  return { id: r.report_id, token: r.token, expires_at: r.expires_at, recipient: null, sent: send };
}

export async function zieheBerichtZurueck(api: Api, reportId: string): Promise<void> {
  await api.rpc("brand_report_revoke", { p_report: reportId });
}

export async function speichereMarkenEinstellungen(api: Api, orgId: string, patch: Partial<MarkenEinstellungen>): Promise<void> {
  const rows = await api.update("org_settings", [["org_id", "eq", orgId]], { ...patch }, { columns: "org_id" });
  if (!rows.length) throw new ApiError(t("Speichern nicht möglich: Die Einstellungen pflegt der Admin.", "Saving not possible: the admin maintains the settings."), { code: "42501" });
}
